import { useMemo } from 'react';
import { REWARDS_TUWY_JOURNEY } from '@/src/constants/feature-flags';
import { useDecision } from '@/src/utils/optimizely';
import { useBeyondAccountStatusQuery } from '@/src/api/use-beyond-account-status';
import { BeyondAccountStatus } from '@/src/types/Response';

interface RewardsFeature {
  enabled: boolean;
  trackingData: {
    [REWARDS_TUWY_JOURNEY]: string | null;
  } | null;
}

enum VariationKey {
  On = 'on',
  Off = 'off',
  ActivatedRewardsVisible = 'activated_rewards_visible',
  ActivatedRewardsHidden = 'activated_rewards_hidden',
}

/**
 * Determine whether to show Beyond rewards cards
 * - If the customer is not ACTIVATED → show rewards (experiment ignored)
 * - If the customer is ACTIVATED show rewards only if experiment is ON (the `variationKey` is `activated_rewards_visible`)
 */
export const useRewardsFeature = (): RewardsFeature => {
  const { data: beyondAccountStatusData } = useBeyondAccountStatusQuery();
  const beyondAccountStatus =
    beyondAccountStatusData?.beyondAccountStatus?.status;

  const [{ enabled, variationKey }] = useDecision(REWARDS_TUWY_JOURNEY);

  const isActivatedUser = beyondAccountStatus === BeyondAccountStatus.Activated;
  const shouldShowRewards = isActivatedUser
    ? variationKey === VariationKey.ActivatedRewardsVisible
    : enabled;

  return useMemo(
    () => ({
      enabled: shouldShowRewards,
      trackingData:
        enabled && isActivatedUser
          ? {
              [REWARDS_TUWY_JOURNEY]: variationKey,
            }
          : null,
    }),
    [enabled, isActivatedUser, shouldShowRewards, variationKey],
  );
};
